import Cookies from 'js-cookie';
import type { AxiosResponse } from 'axios';
import 'url-search-params-polyfill';
import type { KeyboardEvent } from 'react';
import { getUrlParams } from '@src/common/getUrlParams';
import { FieldPath, FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { parse } from 'date-fns';
import UUID from 'uuid-js';

export type ClassNamespace = (...arg0: string[]) => string;
export const classNamespaceFactory =
  (prefix: string): ClassNamespace =>
  (...values: string[]) =>
    values.map((str) => `planne-widget-${prefix}-${str}`).join(' ');

const saleOriginCodeCookieName = 'sale_origin_code';
const saleOriginCodeCookieExpirationsDays = 1;
const affiliationCodeCookieName = 'affiliation_code';
const affiliationCodeCookieExpirationsDays = 30;
const defaultCurrency = 'R$';

export const loadSaleOrigin = (): string | null => {
  const origin = getUrlParams('origin');

  if (origin) {
    Cookies.set(saleOriginCodeCookieName, origin, { expires: saleOriginCodeCookieExpirationsDays });
    return origin;
  }

  const value = Cookies.get(saleOriginCodeCookieName);
  return value !== undefined ? value : null;
};

export const loadAffiliationCode = (): [string | null, boolean] => {
  const code = getUrlParams('ac');
  const storedValue = Cookies.get(affiliationCodeCookieName);
  const changed = code != null && code !== storedValue;

  if (changed) {
    Cookies.set(affiliationCodeCookieName, code, { expires: affiliationCodeCookieExpirationsDays });
  }

  return [code ?? storedValue ?? null, changed];
};

export const clearSaleOrigin = () => {
  Cookies.remove(saleOriginCodeCookieName);
};

export const formatCents = (num: number, symbol = true) => {
  const value = (num / 100).toFixed(2).replace(/\.(\d{2})$/, (_, m) => `,${m}`);
  return (symbol ? `${defaultCurrency} ` : '') + value;
};

export const infoCents = (cents: number) => {
  const formattedCents = (cents % 100).toString().padEnd(2, '0');

  return {
    currency: defaultCurrency,
    priceInt: Math.floor(cents / 100),
    cents: formattedCents,
  };
};

export const disallowWordCharacters = (ev: KeyboardEvent<HTMLInputElement>) => {
  if (ev.key.match(/[A-Z]/i)) {
    ev.preventDefault();
  }
};

export const extractValues = (form: HTMLFormElement): any => {
  const data = new FormData(form);
  const values: any = {};

  for (const entry of data.entries()) {
    const [key, value] = entry;
    values[key] = value;
  }

  return values;
};

export const parseCardValidity = (date: string) => parse(date, 'MM/yy', new Date());

export const getParsedCardValidityValues = (date: string) => {
  const parsedDate = parseCardValidity(date);

  return {
    validityMonth: (parsedDate.getMonth() + 1).toString(),
    validityYear: parsedDate.getFullYear().toString(),
  };
};

const sliceN = (num: number): string => `00${num}`.slice(-2);

export const formatDateTime = (str: string): string => {
  const d = new Date(str);
  const dateStr = [sliceN(d.getDate()), sliceN(d.getMonth() + 1), d.getFullYear()].join('/');
  const timeStr = [d.getHours(), d.getMinutes()].map(sliceN).join(':');

  return `${dateStr} ${timeStr}`;
};

export const formatDate = (str: string): string =>
  str
    .split('-')
    .reduce((arr, s) => [s, ...arr], [] as string[])
    .join('/');

const minutesInHour = 60;
export const formatMinutes = (n: number): string =>
  `${sliceN(Math.floor(n / minutesInHour))}:${sliceN(n % minutesInHour)}`;

export const convertIsoDateToShortDate = (date: string) => date.split('-').reverse().join('/');

export const getErrorMessage = (errors: { [key: string]: string[] } | undefined, name: string) => {
  if (errors && errors[name]) {
    const messages = errors[name] as string[];
    return messages[0];
  }
  return '';
};

export const openNewPdfTab = (pdf: File) => {
  const newWindow = window.open();

  newWindow!.location = URL.createObjectURL(pdf);
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

export const getFormattedTime = (time: number) => {
  return time.toLocaleString('pt-BR', { minimumIntegerDigits: 2, useGrouping: false });
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const dateWasReached = (date?: string) => {
  if (!date) {
    return true;
  }

  return new Date(date) <= new Date();
};

export const convertGeneralContextErrorToHookForm = <TFieldValues extends FieldValues>(
  field: FieldPath<TFieldValues>,
  setError: UseFormSetError<TFieldValues>,
  contextErrors: { [key: string]: { [key: string]: string[] } },
  context: string,
  hasFormError?: boolean,
  clearErrors?: UseFormClearErrors<FieldValues>,
) => {
  if (context in contextErrors) {
    const subContextErrors = contextErrors[context];

    const errorMessages = Object.values(subContextErrors!);

    if (errorMessages.length) {
      for (const error of errorMessages) {
        setError(field, { type: 'manual', message: error[0] });
      }
    } else if (hasFormError && clearErrors) {
      clearErrors(field);
    }
  }
};

export const convertFieldContextErrorToHookForm = <TFieldValues extends FieldValues>(
  field: FieldPath<TFieldValues>,
  setError: UseFormSetError<TFieldValues>,
  contextErrors: { [key: string]: { [key: string]: string[] } },
  context: string,
) => {
  if (context in contextErrors) {
    const subContextErrors = contextErrors[context];

    if (subContextErrors && field in subContextErrors) {
      const fieldErrorMessages = subContextErrors[field];
      const errorMessages = fieldErrorMessages ? fieldErrorMessages : [];

      for (const error of errorMessages) {
        setError(field, { type: 'manual', message: error });
      }
    }
  }
};

export const getRandomString = (length: number) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
};

export const generatePassword = (length: number) => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const characters = uppercase + lowercase + numbers;
  let result = '';

  // Generate random password
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  let usedIndex = -1;

  // Ensure at least one uppercase letter
  if (!/[A-Z]/.test(result)) {
    usedIndex = Math.floor(Math.random() * length);
    result =
      result.slice(0, usedIndex) +
      uppercase.charAt(Math.floor(Math.random() * uppercase.length)) +
      result.slice(usedIndex + 1);
  }

  // Ensure at least one lowercase letter
  if (!/[a-z]/.test(result)) {
    let randomIndex = Math.floor(Math.random() * length);
    // Avoid overwriting the uppercase letter inserted above.
    // Bug fix: when both uppercase and lowercase were missing (e.g., all numbers),
    // using the same random index for both insertions would cause the lowercase
    // to overwrite the uppercase, failing password validation requirements.
    if (randomIndex === usedIndex) {
      randomIndex = (randomIndex + 1) % length;
    }
    result =
      result.slice(0, randomIndex) +
      lowercase.charAt(Math.floor(Math.random() * lowercase.length)) +
      result.slice(randomIndex + 1);
  }

  return result;
};

export const getUtmsFromUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utms: { [key: string]: string } = {};

  for (const key of urlParams.keys()) {
    if (key.startsWith('utm_')) {
      utms[key] = urlParams.get(key) ?? '';
    }
  }

  return utms;
};

export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('@planne-session-id');
  if (!sessionId) {
    sessionId = UUID.create().toString();
    sessionStorage.setItem('@planne-session-id', sessionId);
  }

  return sessionId;
};

export const getStoredUtms = () => {
  // get utm params from session storage and from url.
  let utms: { [key: string]: string } = {};
  const sessionUtms = sessionStorage.getItem('@planne-utms');
  if (sessionUtms) {
    utms = JSON.parse(sessionUtms);
  }

  const urlUtms = getUtmsFromUrlParams();

  // if there are utm params in the url, they will overwrite the session storage
  if (Object.keys(urlUtms).length > 0) {
    Object.keys(urlUtms).forEach((key) => {
      utms[key] = urlUtms[key] ?? '';
    });
  }

  return utms;
};

export const appendQueryParams = (url: string | undefined, params: { [key: string]: string }): string | undefined => {
  const paramsEntries = Object.entries(params ?? {});

  if (!url || url === '' || paramsEntries.length === 0) {
    return url;
  }

  // check if url have ? or & to append the correct character
  const urlHasParams = url.includes('?');

  if (!urlHasParams) {
    url += '?';
  }

  // Append each key-value pair from the params object to the URL search parameters
  paramsEntries.forEach(([key, value], index) => {
    if (index === 0 && !urlHasParams) {
      url += `${key}=${value}`;
    } else {
      url += `&${key}=${value}`;
    }
  });

  // Return the updated URL as a string
  return url;
};

export const getPaginationTotalCount = (resp: AxiosResponse) =>
  parseInt(resp.headers['x-pagination-total-count'] ?? '0', 10);
